import React, { useState, useEffect } from 'react';
import { Settings, CheckCircle, Users, Zap, Loader2 } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { fetchWithTenant } from '../utils/apiClient';
import configs from '../config';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';

const API_BASE_URL = `${configs.BACKEND_URL}/api/tenant`;

const WORKFLOW_MODES = [
  {
    value: 'auto_approve',
    label: 'Auto-Approve',
    description: 'All proposals are immediately approved. Best for solo freelancers or small teams that don\'t need a review step.',
    icon: Zap,
  },
  {
    value: 'human_review',
    label: 'Human Review',
    description: 'All proposals require admin review before submission. Best for teams that need quality control on every proposal.',
    icon: Users,
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    description: 'Admins and superusers auto-approve their own proposals. Regular users submit for review. Best for mixed teams.',
    icon: Settings,
  },
];

export function WorkflowSettings() {
  const { tenant, refreshTenant } = useTenant();
  const [workflowMode, setWorkflowMode] = useState('hybrid');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tenant?.settings?.workflowMode) {
      setWorkflowMode(tenant.settings.workflowMode);
    }
  }, [tenant]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      const response = await fetchWithTenant(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        body: JSON.stringify({ workflowMode }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save settings');
      }

      await refreshTenant();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const hasChanged = workflowMode !== (tenant?.settings?.workflowMode || 'hybrid');

  return (
    <AdminPageLayout>
      <AdminCard
        title="Proposal Workflow"
        description="Choose how proposals are handled before submission. This affects all new proposals created through the app and extension."
        bodyClassName="space-y-4"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {WORKFLOW_MODES.map((mode) => {
            const Icon = mode.icon;
            const isSelected = workflowMode === mode.value;

            return (
              <button
                key={mode.value}
                type="button"
                onClick={() => setWorkflowMode(mode.value)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 p-1.5 rounded-md ${isSelected ? 'bg-primary-100' : 'bg-gray-100'}`}>
                    <Icon className={`h-4 w-4 ${isSelected ? 'text-primary-600' : 'text-gray-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                        {mode.label}
                      </span>
                      {isSelected && (
                        <span className="text-[10px] font-medium text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded">
                          Selected
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{mode.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
          {saved && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <CheckCircle className="h-3.5 w-3.5" />
              Saved
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanged || saving}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </AdminCard>
    </AdminPageLayout>
  );
}

export default WorkflowSettings;
